import { useState } from "react";
import { enGB, et } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { CalendarDays, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CONTROL_FIELD_CLASS } from "@/components/ui/control-scale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type {
  DocumentHistoryDateFilter as DateFilterValue,
  DocumentHistoryDatePreset,
} from "@/domains/documents/history/documentHistoryPresentation";
import type { DocumentTranslations } from "@/lib/i18n/types/documents";
import { cn } from "@/lib/utils";

type DateFilterCopy = DocumentTranslations["log"]["history"]["dateFilter"];

const PRESET_CLASS = "h-7 rounded-md border-border bg-white px-2.5 text-xs font-normal";

interface DocumentHistoryDateFilterProps {
  value: DateFilterValue | null;
  onChange: (value: DateFilterValue | null) => void;
  copy: DateFilterCopy;
  language: string;
}

function formatDay(date: Date, language: string): string {
  return date.toLocaleDateString(language === "et" ? "et-EE" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function presetLabel(preset: DocumentHistoryDatePreset, copy: DateFilterCopy): string {
  if (preset === "today") return copy.today;
  return preset === "7d" ? copy.last7Days : copy.last30Days;
}

function documentHistoryDateFilterLabel(
  value: DateFilterValue | null,
  copy: DateFilterCopy,
  language: string
): string {
  if (!value) return copy.anyTime;
  if (value.kind === "preset") return presetLabel(value.preset, copy);
  const [from, to] = value.from <= value.to ? [value.from, value.to] : [value.to, value.from];
  if (from.toDateString() === to.toDateString()) return formatDay(from, language);
  return `${formatDay(from, language)} – ${formatDay(to, language)}`;
}

function DateFilterTrigger({
  value,
  copy,
  label,
}: {
  value: DateFilterValue | null;
  copy: DateFilterCopy;
  label: string;
}) {
  return (
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={`${copy.label}: ${label}`}
        className={cn(
          CONTROL_FIELD_CLASS,
          "flex w-full min-w-0 items-center justify-between gap-1.5 rounded-md border border-input bg-background ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          value && "border-[#ef4129] bg-[#fbeee9]"
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <CalendarDays
            className={cn("h-3.5 w-3.5 shrink-0 opacity-60", value && "text-[#ef4129]")}
            aria-hidden="true"
          />
          <span className="truncate">{label}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
      </button>
    </PopoverTrigger>
  );
}

function DateFilterPresets({
  copy,
  commit,
}: {
  copy: DateFilterCopy;
  commit: (value: DateFilterValue | null) => void;
}) {
  const presets: DocumentHistoryDatePreset[] = ["today", "7d", "30d"];
  return (
    <div className="space-y-2 border-b px-3 py-2.5">
      <p className="text-xs font-semibold">{copy.label}</p>
      <div className="flex flex-wrap gap-1">
        <Button variant="outline" size="sm" className={PRESET_CLASS} onClick={() => commit(null)}>
          {copy.anyTime}
        </Button>
        {presets.map((preset) => (
          <Button
            key={preset}
            variant="outline"
            size="sm"
            className={PRESET_CLASS}
            onClick={() => commit({ kind: "preset", preset })}
          >
            {presetLabel(preset, copy)}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * The Document history panel's date control: presets that always mean "up to
 * now", plus a calendar for a custom range of whole local days. It filters the
 * history already loaded; older events arrive through the panel's "Load older".
 */
export function DocumentHistoryDateFilter(props: DocumentHistoryDateFilterProps) {
  const { value, onChange, copy, language } = props;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();
  const label = documentHistoryDateFilterLabel(value, copy, language);

  const commit = (next: DateFilterValue | null) => {
    onChange(next);
    setOpen(false);
  };
  const applyCustom = () => {
    if (!draft?.from) return;
    commit({ kind: "custom", from: draft.from, to: draft.to ?? draft.from });
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDraft(value?.kind === "custom" ? { from: value.from, to: value.to } : undefined);
        }
      }}
    >
      <DateFilterTrigger value={value} copy={copy} label={label} />
      <PopoverContent align="end" className="w-auto p-0">
        <DateFilterPresets copy={copy} commit={commit} />
        <p className="px-3 pt-2.5 text-xs text-muted-foreground">{copy.customRange}</p>
        <div className="flex justify-center">
          <Calendar
            mode="range"
            locale={language === "et" ? et : enGB}
            selected={draft}
            onSelect={setDraft}
            defaultMonth={draft?.from}
            disabled={{ after: new Date() }}
            numberOfMonths={1}
          />
        </div>
        <div className="flex justify-end gap-2 border-t px-3 py-2">
          {value ? (
            <Button variant="ghost" size="sm" onClick={() => commit(null)}>
              {copy.clear}
            </Button>
          ) : null}
          <Button size="sm" onClick={applyCustom} disabled={!draft?.from}>
            {copy.apply}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
